import { ErrorInfo, PropsWithChildren } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ReactErrorBoundary } from './react-error-boundary';
import { ServerErrorState } from './ServerErrorState';
import { AxiosError, isAxiosError } from 'axios';

const trackError = (error: Error | AxiosError, errorInfo: ErrorInfo) => {
    const renderingError = new Error(error.message);
    renderingError.name = 'ReactRenderingError';
    renderingError.stack = errorInfo.componentStack ?? '';
    renderingError.cause = error;

    let errorContext: object = {
        userSawErrorBoundary: true,
    };

    if (isAxiosError(error)) {
        errorContext = {
            ...errorContext,
            requestHeaders: error.config?.headers,
            responseHeaders: error.response?.headers,
        };
    }

    if (window.DD_RUM) {
        window.DD_RUM.addError(renderingError, errorContext);
    }
};

/**
 * Component to catch and display errors in React components
 */
interface ErrorBoundaryProps {
    actionCausingError: string;
    onRetry?: () => void;
    onErrorTracked?: () => void;
}

export const ErrorBoundary = ({
    children,
    actionCausingError,
    onRetry,
    onErrorTracked,
}: PropsWithChildren<ErrorBoundaryProps>) => {
    const queryClient = useQueryClient();

    const handleError = (error: Error | AxiosError, errorInfo: ErrorInfo) => {
        trackError(error, errorInfo);
        onErrorTracked?.();
    };

    return (
        <ReactErrorBoundary
            onError={handleError}
            fallbackRender={({ error, resetErrorBoundary }) => (
                <ServerErrorState
                    errorDetails={{
                        error,
                        actionCausingError,
                    }}
                    onRetry={() => {
                        // Reset queries so they will be refetched when the user retries
                        queryClient.resetQueries();
                        onRetry?.();
                        resetErrorBoundary();
                    }}
                />
            )}
        >
            {children}
        </ReactErrorBoundary>
    );
};
